import { rampExpression } from '@motogo/shared';
import type { ExpressionSpecification, Map, StyleImageInterface } from 'maplibre-gl';

export const SOURCES = {
  GPS: 'gps',
  PEERS: 'peers',
  ROUTE: 'route',
  // Route progress (anchored emphasis): the same route pre-chunked with cumulative
  // distance `s` per feature, so ridden/near/far stretches can be styled independently.
  ROUTE_PROGRESS: 'route-progress',
  CAMERAS_FIXED: 'speed-cameras-fixed',
  CAMERAS_AVG_LINE: 'speed-cameras-avg-line',
  CAMERAS_AVG_POINT: 'speed-cameras-avg-point',
  // Ride review (detail screen): the saved track coloured by speed, and the scrub cursor.
  REVIEW_TRACK: 'review-track',
  REVIEW_CURSOR: 'review-cursor',
  // Route editor (M3): per-segment lines, draggable numbered waypoints, drag/routing ghosts.
  EDITOR_LINE: 'editor-line',
  EDITOR_WAYPOINTS: 'editor-waypoints',
  EDITOR_DRAFT: 'editor-draft',
  // Discover (M4): the search results' overview polylines, focused one highlighted.
  DISCOVER_ROUTES: 'discover-routes',
} as const;

const EMPTY_FC = { type: 'FeatureCollection' as const, features: [] };

// Own-marker colors — the teal/red pairing carried over from the original GPS dot:
// nav-blue blends into the liberty map style, this combination doesn't.
const SELF_COLOR = '#00c896';
const SELF_RING = 'rgba(250, 0, 0, 0.7)';

// Peer badge palette. Deliberately excludes the self teal, the amber UI accent, and the
// camera reds/oranges so rider badges never get confused with other map symbology.
// Index assignment is a stable hash of the rider's name (peerColorIndex).
const PEER_PALETTE = ['#7c4dff', '#2979ff', '#e91e63', '#ff6d00', '#43a047', '#00acc1'];
const PEER_STALE_COLOR = '#9aa0a6';

/** Stable palette index for a rider name, so a rider keeps their color across updates. */
export function peerColorIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % PEER_PALETTE.length;
}

// Generated at runtime so we don't ship a static asset; +x direction so symbol-placement 'line' rotates it forward
function createArrowImage(): ImageData {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 3;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(size * 0.9, size * 0.5);
  ctx.lineTo(size * 0.25, size * 0.15);
  ctx.lineTo(size * 0.4, size * 0.5);
  ctx.lineTo(size * 0.25, size * 0.85);
  ctx.closePath();
  ctx.stroke();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

// Generated at runtime: a colored circular badge with a white camera glyph, so speed
// traps read as an icon rather than a plain dot while keeping per-type color coding.
function createCameraImage(color: string): ImageData {
  const size = 40;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;

  // Badge background
  ctx.beginPath();
  ctx.arc(c, c, size * 0.45, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  // Camera body
  ctx.fillStyle = '#ffffff';
  const bw = size * 0.5;
  const bh = size * 0.34;
  const bx = c - bw / 2;
  const by = c - bh / 2 + size * 0.03;
  const r = 3;
  ctx.beginPath();
  ctx.moveTo(bx + r, by);
  ctx.arcTo(bx + bw, by, bx + bw, by + bh, r);
  ctx.arcTo(bx + bw, by + bh, bx, by + bh, r);
  ctx.arcTo(bx, by + bh, bx, by, r);
  ctx.arcTo(bx, by, bx + bw, by, r);
  ctx.closePath();
  // Viewfinder bump on top
  ctx.rect(c - size * 0.07, by - size * 0.07, size * 0.14, size * 0.08);
  ctx.fill();

  // Lens
  ctx.beginPath();
  ctx.arc(c, by + bh / 2, bh * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  return ctx.getImageData(0, 0, size, size);
}

// Own marker while moving: a navigation pointer drawn tip-up (north) so 'icon-rotate'
// can take the GPS heading directly.
function createNavArrowImage(): ImageData {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(c, size * 0.08); // tip
  ctx.lineTo(size * 0.84, size * 0.84); // right wing
  ctx.lineTo(c, size * 0.62); // tail notch
  ctx.lineTo(size * 0.16, size * 0.84); // left wing
  ctx.closePath();
  ctx.strokeStyle = SELF_RING;
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.fillStyle = SELF_COLOR;
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

// Own marker while stationary: a dot with a soft expanding halo. Animated via the
// StyleImageInterface render() contract — MapLibre calls it per frame while the image is
// on screen (the moving/stationary layer filters mean it costs nothing while riding).
function createPulsingDotImage(map: Map): StyleImageInterface {
  const size = 120;
  let context: CanvasRenderingContext2D | null = null;
  return {
    width: size,
    height: size,
    data: new Uint8ClampedArray(size * size * 4),
    onAdd() {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      context = canvas.getContext('2d');
    },
    render() {
      if (!context) return false;
      const duration = 1800;
      const t = (performance.now() % duration) / duration;
      const core = (size / 2) * 0.28;
      const halo = core + (size / 2 - core - 4) * t;

      context.clearRect(0, 0, size, size);
      context.beginPath();
      context.arc(size / 2, size / 2, halo, 0, Math.PI * 2);
      context.fillStyle = `rgba(0, 200, 150, ${0.35 * (1 - t)})`;
      context.fill();

      context.beginPath();
      context.arc(size / 2, size / 2, core, 0, Math.PI * 2);
      context.fillStyle = SELF_COLOR;
      context.strokeStyle = SELF_RING;
      context.lineWidth = 6;
      context.fill();
      context.stroke();

      this.data = context.getImageData(0, 0, size, size).data;
      map.triggerRepaint();
      return true;
    },
  };
}

// Peer badge: colored disc with a white ring; the heading variant adds a tick on the rim
// pointing up, and the whole icon is rotated by the peer's heading (the initials are a
// separate, unrotated text layer so they stay upright).
function createPeerBadgeImage(color: string, withHeadingTick: boolean): ImageData {
  const size = 56;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  const r = size * 0.34;

  if (withHeadingTick) {
    ctx.beginPath();
    ctx.moveTo(c, size * 0.02);
    ctx.lineTo(c - size * 0.13, size * 0.24);
    ctx.lineTo(c + size * 0.13, size * 0.24);
    ctx.closePath();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.fill();
  }

  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 3.5;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  return ctx.getImageData(0, 0, size, size);
}

function addCameraIconLayer(map: Map, id: string, color: string): void {
  const imageId = `${id}-icon`;
  if (!map.hasImage(imageId)) {
    map.addImage(imageId, createCameraImage(color), { pixelRatio: 2 });
  }
  map.addSource(id, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id,
    type: 'symbol',
    source: id,
    layout: {
      'icon-image': imageId,
      'icon-size': 1,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
}

/** Every layer drawing the planned route — MapView hides these while a ride is under review. */
export const ROUTE_LAYER_IDS = [
  'route-line',
  'route-progress-base',
  'route-progress-near',
  'route-arrows',
  'route-point-halo',
  'route-points',
] as const;

/** The anchored-emphasis pair — swapped in for 'route-line' while locked onto the route. */
export const ROUTE_PROGRESS_LAYER_IDS = ['route-progress-base', 'route-progress-near'] as const;

// Anchored-emphasis palette: ridden = grey (distinguishable from what's still to come),
// far-future = muted blue, the near stretch = vivid and drawn on top — on a road the
// route uses twice, the pass nearer along the route wins both contrast and stacking.
export const PROGRESS_RIDDEN_COLOR = '#878d96';
export const PROGRESS_AHEAD_COLOR = '#4f7dd9';
export const PROGRESS_NEAR_COLOR = '#2979ff';

/** Editor line layers, in hit-test order (waypoints are queried separately, first). */
export const EDITOR_LINE_LAYER_IDS = ['editor-line', 'editor-line-straight', 'editor-line-error'] as const;
export const EDITOR_WAYPOINT_LAYER_ID = 'editor-waypoints';
export const DISCOVER_ROUTES_LAYER_ID = 'discover-routes';

// Sideways shift (px) applied to route/editor lines and their arrows — separates the two
// passes of a road the route travels in both directions (each shifts to its own right).
const ROUTE_LINE_OFFSET = 4;

// Editor palette: segments carry a per-chunk `color` sampled from the route gradient
// (MapView computes it — line-gradient can't span multiple features); selection is the
// amber accent; the plain blue is the fallback for features without a colour.
const EDITOR_LINE_COLOR: ExpressionSpecification = [
  'case',
  ['boolean', ['get', 'selected'], false],
  '#ffb020',
  ['coalesce', ['get', 'color'], '#2979ff'],
];

export function addLayersToMap(map: Map): void {
  // Discover results (M4) — under everything else: thin semi-transparent candidates, the
  // focused one amber and full-width. One source, data-driven styling on `focused`.
  map.addSource(SOURCES.DISCOVER_ROUTES, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: DISCOVER_ROUTES_LAYER_ID,
    type: 'line',
    source: SOURCES.DISCOVER_ROUTES,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['case', ['boolean', ['get', 'focused'], false], '#ffb020', '#8ab4f8'],
      'line-width': ['case', ['boolean', ['get', 'focused'], false], 5, 3],
      'line-opacity': ['case', ['boolean', ['get', 'focused'], false], 1, 0.55],
    },
  });

  // Route — rendered below cameras and GPS dot
  // lineMetrics is required by the line-gradient expression below
  map.addSource(SOURCES.ROUTE, { type: 'geojson', data: EMPTY_FC, lineMetrics: true });
  map.addLayer({
    id: 'route-line',
    type: 'line',
    source: SOURCES.ROUTE,
    paint: {
      'line-width': 6,
      'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, '#0000fa', 1, '#66ccff'],
      // Every line shifts a hair to its own right. Invisible on a normal route, but where
      // the route doubles back on the same road the two opposite-direction passes each
      // shift to *their* right and separate into two parallel lines — out-and-back
      // stretches become readable at a glance. Same-direction reuse stays overlapped,
      // which is fine: no direction ambiguity there.
      'line-offset': ROUTE_LINE_OFFSET,
    },
  });
  // Anchored route-progress emphasis (hidden until useRouteProgress locks on): a muted
  // base pass (MapView repaints ridden-grey vs ahead-blue around the rider's progress)
  // and a vivid near-window pass drawn above it (MapView drives its `s` filter). Added
  // before the arrows so direction stays readable on every band.
  map.addSource(SOURCES.ROUTE_PROGRESS, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'route-progress-base',
    type: 'line',
    source: SOURCES.ROUTE_PROGRESS,
    layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
    paint: {
      'line-color': PROGRESS_AHEAD_COLOR,
      'line-width': 4,
      'line-opacity': 0.55,
      'line-offset': ROUTE_LINE_OFFSET,
    },
  });
  map.addLayer({
    id: 'route-progress-near',
    type: 'line',
    source: SOURCES.ROUTE_PROGRESS,
    filter: ['boolean', false], // MapView sets the live s-window filter
    layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
    paint: {
      'line-color': PROGRESS_NEAR_COLOR,
      'line-width': 6,
      'line-offset': ROUTE_LINE_OFFSET,
    },
  });
  if (!map.hasImage('route-arrow')) {
    map.addImage('route-arrow', createArrowImage(), { pixelRatio: 2 });
  }
  map.addLayer({
    id: 'route-arrows',
    type: 'symbol',
    source: SOURCES.ROUTE,
    filter: ['==', '$type', 'LineString'],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 100,
      'icon-image': 'route-arrow',
      'icon-size': 1,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      // Follow the offset line: icon-offset applies in the icon's rotated frame, so
      // +y tracks the line's own "right" the same way line-offset does.
      'icon-offset': [0, ROUTE_LINE_OFFSET],
    },
  });
  // Destination marker (A→B end point). Soft amber glow under a crisp amber dot with a
  // white ring — matches the review-cursor marker language (accent #ffb020), replacing
  // the legacy oversized blue/red circle.
  map.addLayer({
    id: 'route-point-halo',
    type: 'circle',
    source: SOURCES.ROUTE,
    filter: ['==', '$type', 'Point'],
    paint: {
      'circle-radius': 16,
      'circle-color': '#ffb020',
      'circle-opacity': 0.22,
      'circle-blur': 0.5,
    },
  });
  map.addLayer({
    id: 'route-points',
    type: 'circle',
    source: SOURCES.ROUTE,
    filter: ['==', '$type', 'Point'],
    paint: {
      'circle-radius': 8,
      'circle-color': '#ffb020',
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 3,
    },
  });

  // Ride review track — the saved ride coloured by the active metric. line-gradient can't
  // read per-vertex data, so the track arrives as per-segment LineString features carrying
  // the metric value as `v` (trackToSegments) and we colour with a data-driven line-color
  // ramp. Defaults to speed; MapView swaps the ramp + data when the metric changes.
  map.addSource(SOURCES.REVIEW_TRACK, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'review-track',
    type: 'line',
    source: SOURCES.REVIEW_TRACK,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-width': 5,
      'line-color': rampExpression('speed') as ExpressionSpecification,
    },
  });

  // Route editor (M3). Draft first (under everything of the editor's): dashed grey ghosts
  // for segments still routing and the elastic line while dragging a waypoint. Then the
  // per-segment route lines — straight segments get their own dashed layer because
  // line-dasharray isn't data-driven — and the numbered waypoint discs on top.
  map.addSource(SOURCES.EDITOR_DRAFT, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'editor-draft',
    type: 'line',
    source: SOURCES.EDITOR_DRAFT,
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': '#9aa0a6', 'line-width': 4, 'line-dasharray': [1, 2] },
  });
  map.addSource(SOURCES.EDITOR_LINE, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'editor-line',
    type: 'line',
    source: SOURCES.EDITOR_LINE,
    filter: ['all', ['!', ['to-boolean', ['get', 'straight']]], ['!', ['to-boolean', ['get', 'failed']]]],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': EDITOR_LINE_COLOR, 'line-width': 6, 'line-offset': ROUTE_LINE_OFFSET },
  });
  map.addLayer({
    id: 'editor-line-straight',
    type: 'line',
    source: SOURCES.EDITOR_LINE,
    filter: ['all', ['to-boolean', ['get', 'straight']], ['!', ['to-boolean', ['get', 'failed']]]],
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': EDITOR_LINE_COLOR, 'line-width': 5, 'line-dasharray': [0.8, 1.6], 'line-offset': ROUTE_LINE_OFFSET },
  });
  // Failed segments: a red dashed beeline in the *selectable* source — tap it to retry or
  // switch its mode, instead of having to delete and re-add the waypoint.
  map.addLayer({
    id: 'editor-line-error',
    type: 'line',
    source: SOURCES.EDITOR_LINE,
    filter: ['to-boolean', ['get', 'failed']],
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': ['case', ['boolean', ['get', 'selected'], false], '#ffb020', '#ff453a'] as ExpressionSpecification,
      'line-width': 5,
      'line-dasharray': [0.8, 1.6],
      'line-offset': ROUTE_LINE_OFFSET,
    },
  });
  // Direction arrows, same glyph as the planned-route line — an editing session reads
  // its riding direction at a glance (and reverse shows its effect immediately).
  map.addLayer({
    id: 'editor-line-arrows',
    type: 'symbol',
    source: SOURCES.EDITOR_LINE,
    filter: ['!', ['to-boolean', ['get', 'failed']]],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 100,
      'icon-image': 'route-arrow',
      'icon-size': 1,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-offset': [0, ROUTE_LINE_OFFSET],
    },
  });
  map.addSource(SOURCES.EDITOR_WAYPOINTS, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: EDITOR_WAYPOINT_LAYER_ID,
    type: 'circle',
    source: SOURCES.EDITOR_WAYPOINTS,
    paint: {
      'circle-radius': 12,
      'circle-color': ['case', ['boolean', ['get', 'selected'], false], '#ffb020', '#ffffff'],
      'circle-stroke-color': '#14171d',
      'circle-stroke-width': 2.5,
    },
  });
  map.addLayer({
    id: 'editor-waypoint-labels',
    type: 'symbol',
    source: SOURCES.EDITOR_WAYPOINTS,
    layout: {
      'text-field': ['get', 'label'],
      'text-font': ['Noto Sans Bold'],
      'text-size': 12,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: { 'text-color': '#14171d' },
  });

  // Speed camera layers — data populated in Phase 3
  map.addSource(SOURCES.CAMERAS_AVG_LINE, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: SOURCES.CAMERAS_AVG_LINE,
    type: 'line',
    source: SOURCES.CAMERAS_AVG_LINE,
    paint: { 'line-color': '#ffaa00', 'line-width': 4, 'line-dasharray': [6, 4] },
  });
  addCameraIconLayer(map, SOURCES.CAMERAS_AVG_POINT, '#ffaa00');
  addCameraIconLayer(map, SOURCES.CAMERAS_FIXED, '#ff4400');

  // Group riders (Phase 5) — below our own marker. Features carry `name`, `initials`,
  // a palette `color` index, `heading`/`hasHeading`, and a `stale` flag (no update for a
  // while: tunnel, app backgrounded, dead zone) that greys the badge out.
  const staleCase = (fresh: string | number, stale: string | number): ExpressionSpecification => [
    'case',
    ['boolean', ['get', 'stale'], false],
    stale,
    fresh,
  ];
  PEER_PALETTE.forEach((color, i) => {
    map.addImage(`peer-badge-${i}`, createPeerBadgeImage(color, false), { pixelRatio: 2 });
    map.addImage(`peer-badge-${i}-dir`, createPeerBadgeImage(color, true), { pixelRatio: 2 });
  });
  map.addImage('peer-badge-stale', createPeerBadgeImage(PEER_STALE_COLOR, false), { pixelRatio: 2 });
  map.addImage('peer-badge-stale-dir', createPeerBadgeImage(PEER_STALE_COLOR, true), { pixelRatio: 2 });

  map.addSource(SOURCES.PEERS, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'peer-badges',
    type: 'symbol',
    source: SOURCES.PEERS,
    layout: {
      'icon-image': [
        'concat',
        'peer-badge-',
        ['case', ['boolean', ['get', 'stale'], false], 'stale', ['to-string', ['get', 'color']]],
        ['case', ['boolean', ['get', 'hasHeading'], false], '-dir', ''],
      ],
      'icon-size': 1,
      // Rotating the badge is safe — it's rotationally uniform except the heading tick;
      // the initials live in their own unrotated layer below.
      'icon-rotate': ['get', 'heading'],
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: { 'icon-opacity': staleCase(1, 0.75) },
  });
  map.addLayer({
    id: 'peer-initials',
    type: 'symbol',
    source: SOURCES.PEERS,
    layout: {
      'text-field': ['get', 'initials'],
      'text-font': ['Noto Sans Bold'],
      'text-size': 11,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#ffffff',
      'text-opacity': staleCase(1, 0.8),
    },
  });
  map.addLayer({
    id: 'peer-labels',
    type: 'symbol',
    source: SOURCES.PEERS,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Bold'],
      'text-size': 13,
      // Below the badge — keeps clear of the heading tick orbiting the rim.
      'text-anchor': 'top',
      'text-offset': [0, 1.3],
      // When riders bunch up at a stop, colliding labels hide rather than overlap into
      // an unreadable pile — the badges still show everyone.
      'text-optional': true,
    },
    paint: {
      'text-color': staleCase('#ffffff', '#c9cdd2'),
      'text-halo-color': 'rgba(20, 24, 30, 0.9)',
      'text-halo-width': 1.5,
    },
  });

  // Own marker — topmost. Two layers filtered on `moving`: a heading arrow while riding,
  // a pulsing dot while stationary (or when the fix has no usable heading).
  map.addImage('gps-arrow', createNavArrowImage(), { pixelRatio: 2 });
  map.addImage('gps-pulse', createPulsingDotImage(map), { pixelRatio: 2 });
  map.addSource(SOURCES.GPS, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'gps-marker-stationary',
    type: 'symbol',
    source: SOURCES.GPS,
    filter: ['!', ['to-boolean', ['get', 'moving']]],
    layout: {
      'icon-image': 'gps-pulse',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
  map.addLayer({
    id: 'gps-marker-moving',
    type: 'symbol',
    source: SOURCES.GPS,
    filter: ['to-boolean', ['get', 'moving']],
    layout: {
      'icon-image': 'gps-arrow',
      'icon-rotate': ['get', 'heading'],
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });

  // Ride review scrub cursor — topmost so nothing hides it.
  map.addSource(SOURCES.REVIEW_CURSOR, { type: 'geojson', data: EMPTY_FC });
  map.addLayer({
    id: 'review-cursor',
    type: 'circle',
    source: SOURCES.REVIEW_CURSOR,
    paint: {
      'circle-radius': 8,
      'circle-color': '#ffb020',
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 3,
    },
  });
}
